/**
 * P4-WP-8C.3: the canonical Account invitation role vocabulary.
 *
 * AdminConsole previously carried TWO invite dropdowns with different option
 * lists. One offered `agent` under the label "Sales Agent"; the other offered
 * `member`. Both are legacy read-compatibility values that no write path may
 * produce, and having two lists is what let them drift apart in the first
 * place -- so the options live here once, and the dialog renders whatever this
 * module says.
 *
 * Pure data and pure functions only: no policy decisions the backend does not
 * also make. `can_assign_role` remains authoritative; this exists so the UI
 * does not OFFER something the server will refuse, not to grant anything.
 */

/** The canonical writable Account roles, in administrative order. */
export const CANONICAL_INVITE_ROLES = Object.freeze([
  Object.freeze({ value: "owner", label: "Owner" }),
  Object.freeze({ value: "admin", label: "Admin" }),
  Object.freeze({ value: "manager", label: "Manager" }),
  Object.freeze({ value: "sales_agent", label: "Sales Agent" }),
  Object.freeze({ value: "booking_agent", label: "Booking Agent" }),
  Object.freeze({ value: "customer_service_agent", label: "Customer Service Agent" }),
]);

/**
 * The role a fresh invitation starts on.
 *
 * Deliberately an operational role rather than an administrative one: the
 * previous defaults were `member` on mount and `agent` after any submit or
 * cancel, so the most common invitation carried the most privileged legacy
 * value the resolver would accept. Defaulting low is the safer failure.
 */
export const DEFAULT_INVITE_ROLE = "sales_agent";

/** Legacy values that must never leave this surface, kept for assertions. */
export const NON_INVITABLE_ROLES = Object.freeze([
  "member",
  "agent",
  "super_admin",
]);

/**
 * Which roles this actor may offer.
 *
 * Only an Owner may create an Owner -- an Admin promoting an ally who could
 * promote them back is escalation by proxy, so the boundary sits with role
 * creation rather than with the final-Owner count, which would happily permit
 * a fourth Owner.
 */
export function inviteRoleOptionsForActor(actorRole) {
  if (actorRole === "owner") {
    return CANONICAL_INVITE_ROLES;
  }
  if (actorRole === "admin") {
    return CANONICAL_INVITE_ROLES.filter((option) => option.value !== "owner");
  }
  // Manager and operational roles hold no invitation authority. Returning an
  // empty list rather than a default keeps this aligned with the server, which
  // refuses them outright.
  return [];
}

/**
 * Keep a selected role legal for the actor.
 *
 * An Admin opening the dialog while `owner` is selected -- possible if the
 * value survived from another context -- must not be able to submit it.
 */
export function resolveInviteRole(actorRole, candidate) {
  const allowed = inviteRoleOptionsForActor(actorRole);
  if (allowed.some((option) => option.value === candidate)) {
    return candidate;
  }
  if (allowed.some((option) => option.value === DEFAULT_INVITE_ROLE)) {
    return DEFAULT_INVITE_ROLE;
  }
  return allowed.length ? allowed[0].value : DEFAULT_INVITE_ROLE;
}
